import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Select,
  Spin,
  Typography,
} from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  LocalCoordinatorSettingsResponse,
  LocalCoordinatorSettingsValues,
  NotificationsResponse,
} from "../api/types";

/**
 * Sidebar entry "local_coord_settings" → /congregation/local_coordinator_settings.
 *
 * The coordinator's own settings, not the congregation's — those live on
 * /congregation/congregation_settings and need full access, while these can be
 * maintained by an observer.
 *
 * The two preferences at the bottom steer the speaker search on the assignment
 * screens: whether the congregation's own speakers appear in it at all, and in
 * what order the candidates are listed.
 */
export function LocalCoordinatorSettingsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<LocalCoordinatorSettingsValues>();

  const settings = useQuery({
    queryKey: ["congregation", "local_coordinator_settings"],
    queryFn: () =>
      apiFetch<LocalCoordinatorSettingsResponse>("/congregation/local_coordinator_settings"),
  });

  useEffect(() => {
    if (!settings.data) return;

    const { role, local_coordinator: coordinator, user } = settings.data.data;
    form.setFieldsValue({
      role_events: role.role_events,
      contact_person: role.contact_person,
      hide_own_speakers: coordinator.hide_own_speakers,
      speaker_speech_sorting_mode: coordinator.speaker_speech_sorting_mode,
      phone: user.phone ?? "",
      mobile: user.mobile ?? "",
      private_email: user.private_email ?? "",
    });
  }, [settings.data, form]);

  const update = useMutation({
    mutationFn: (values: LocalCoordinatorSettingsValues) =>
      apiFetch<NotificationsResponse>("/congregation/local_coordinator_settings", {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({
        queryKey: ["congregation", "local_coordinator_settings"],
      });
      // contact_person decides who shows up on the congregation contact screen.
      void queryClient.invalidateQueries({ queryKey: ["congregation", "contacts"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  if (settings.isPending) return <Spin />;

  if (settings.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          settings.error instanceof Error ? settings.error.message : String(settings.error)
        }
      />
    );
  }

  const observer = settings.data?.data.role.observer ?? false;
  const sortingModes = settings.data?.data.sorting_modes ?? [];
  const hint = (text: string) => <Typography.Text type="secondary">{text}</Typography.Text>;

  return (
    <Card title={t("navigation.local_coord_settings")} style={{ maxWidth: 720 }}>
      <Form<LocalCoordinatorSettingsValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => update.mutate(values)}
      >
        <Divider titlePlacement="start">
          {t("role.headers.address")}
        </Divider>

        <Form.Item name="phone" label={t("activerecord.attributes.role.phone")}>
          <Input />
        </Form.Item>

        <Form.Item name="mobile" label={t("activerecord.attributes.role.mobile")}>
          <Input />
        </Form.Item>

        <Form.Item
          name="private_email"
          label={t("activerecord.attributes.role.private_email")}
          rules={[{ type: "email" }]}
          extra={hint(t("local_coordinator.using_user_email"))}
        >
          <Input />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("role.headers.settings")}
        </Divider>

        <Form.Item>
          <Checkbox checked={observer} disabled>
            {t("activerecord.attributes.role.observer")}
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="role_events"
          valuePropName="checked"
          extra={hint(t("role.hints.role_events"))}
        >
          <Checkbox>{t("activerecord.attributes.role.role_events")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="contact_person"
          valuePropName="checked"
          extra={hint(t("role.hints.contact_person"))}
        >
          <Checkbox>{t("activerecord.attributes.role.contact_person")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="speaker_speech_sorting_mode"
          label={t("activerecord.attributes.local_coordinator.speaker_speech_sorting_mode")}
          extra={hint(t("local_coordinator.hints.speaker_speech_sorting_mode"))}
        >
          <Select options={sortingModes} style={{ width: 320 }} />
        </Form.Item>

        <Form.Item
          name="hide_own_speakers"
          valuePropName="checked"
          extra={hint(t("local_coordinator.hints.hide_own_speakers"))}
        >
          <Checkbox>
            {t("activerecord.attributes.local_coordinator.hide_own_speakers")}
          </Checkbox>
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t("form.update")}
        </Button>
      </Form>
    </Card>
  );
}
